<script lang="ts">
  import IconButton from '@smui/icon-button';
  import Tooltip, { Wrapper } from '@smui/tooltip';
  import { onMount } from 'svelte';

  import {
    Editor,
    commandsCtx,
    defaultValueCtx,
    editorViewCtx,
    editorViewOptionsCtx,
    rootCtx,
    type CmdKey,
  } from '@milkdown/core';
  import type { Ctx } from '@milkdown/ctx';
  import { clipboard } from '@milkdown/plugin-clipboard';
  import { cursor } from '@milkdown/plugin-cursor';
  import { history, redoCommand, undoCommand } from '@milkdown/plugin-history';
  import { listener, listenerCtx } from '@milkdown/plugin-listener';
  import { prism, prismConfig } from '@milkdown/plugin-prism';
  import { trailing } from '@milkdown/plugin-trailing';
  import {
    commonmark,
    createCodeBlockCommand,
    toggleEmphasisCommand,
    toggleStrongCommand,
    wrapInBlockquoteCommand,
    wrapInBulletListCommand,
    wrapInHeadingCommand,
    wrapInOrderedListCommand,
  } from '@milkdown/preset-commonmark';
  import { gfm, toggleStrikethroughCommand } from '@milkdown/preset-gfm';
  import { nord } from '@milkdown/theme-nord';
  import { insert } from '@milkdown/utils';
  import csharp from 'refractor/lang/csharp';

  import style from '@milkdown/theme-nord/style.css?inline';
  import { useNodeViewFactory, useWidgetViewFactory } from '@prosemirror-adapter/svelte';
  import prismTheme from 'prism-themes/themes/prism-xonokai.css?inline';
  import prose from 'prosemirror-tables/style/tables.css?inline';
  import type { FileUpload } from '../../firebase/common/feed';
  import PickImage from './PickImage.svelte';

  import { components } from './editor_components';

  export let content: string;
  export let freeToRead: string | null = null;
  export let readingText: string | null = null;
  export let readonly: boolean = false;
  export let label: string | null = null;
  export let images: FileUpload[] = [];
  let imagePicker: PickImage;
  let activeEditor: Editor | null = null;

  const viewFactory = useNodeViewFactory();
  const widgetFactory = useWidgetViewFactory();

  onMount(() => {
    return () => {
      activeEditor?.destroy();
    };
  });

  function editable() {
    return !readonly;
  }

  function editor(dom: HTMLDivElement) {
    activeEditor?.destroy();

    Editor.make()
      .config((ctx) => {
        ctx.set(rootCtx, dom);
        ctx.set(defaultValueCtx, content);
        ctx.update(editorViewOptionsCtx, (prev) => ({
          ...prev,
          editable,
        }));
        ctx.get(listenerCtx).markdownUpdated((ctx, markdown, prevMarkdown) => {
          content = markdown.replaceAll(/!\[\]\(a\)/ig, '');
          readingText = markdown.split('\n')[0] ?? '';

          let index: number;
          if ((index = markdown.search(/^\s*:\s*more\s*:$/i)) >= 0) {
            freeToRead = markdown.substring(0, index);
          } else {
            freeToRead = null;
          }
        });

        ctx.set(prismConfig.key, {
          configureRefractor: (refractor) => {
            refractor.register(csharp);
            refractor.alias({ cs: 'csharp' });
          },
        });
      })
      .config(nord)
      .use(commonmark)
      .use(gfm)
      .use(listener)
      .use(clipboard)
      .use(history)
      .use(prism)
      .use(cursor)
      .use(trailing)
      .use(components(viewFactory, widgetFactory))
      .create()
      .then((it) => {
        activeEditor = it;
        return it;
      });
  }

  function call<T>(slice: CmdKey<T>, payload?: T) {
    return function (ctx: Ctx) {
      ctx.get(commandsCtx).call(slice, payload);
    };
  }
</script>

<svelte:head>
  <link rel="stylesheet" href="data:text/css;base64,{btoa(style)}" />
  <link rel="stylesheet" href="data:text/css;base64,{btoa(prose)}" />
  <link rel="stylesheet" href="data:text/css;base64,{btoa(prismTheme)}" />
</svelte:head>

<PickImage {images} bind:this={imagePicker} />

<div class="editor-wrapper" class:readonly>
  {#if !readonly}
    <div class="editor-heading">
      {#if label}
        <div class="editor-label">{label}</div>
      {/if}
      <div class="markdown-help">
        <a
          href="https://docs.github.com/ja/get-started/writing-on-github/getting-started-with-writing-and-formatting-on-github/basic-writing-and-formatting-syntax"
          target="_blank"
        >
          参考: markdownの書き方 (外部のサイト)
        </a>
      </div>
    </div>
  {/if}
  <div class="editor-root" aria-label={label} use:editor>
    {#if !readonly}
      <div class="toolbar">
        <Wrapper class="tool">
          <IconButton
            class="material-symbols-outlined"
            type="button"
            size="mini"
            on:click={() => activeEditor?.action(call(undoCommand.key))}
          >
            undo
          </IconButton>
          <Tooltip>Undo (<code class="keystroke">ctrl + Z</code>/<code class="keystroke">cmd + Z</code>)</Tooltip>
        </Wrapper>
        <Wrapper class="tool">
          <IconButton
            class="material-symbols-outlined"
            type="button"
            size="mini"
            on:click={() => activeEditor?.action(call(redoCommand.key))}
          >
            redo
          </IconButton>
          <Tooltip
            >Redo (<code class="keystroke">ctrl + shift + Z</code>/<code class="keystroke">cmd + shift + Z</code
            >)</Tooltip
          >
        </Wrapper>
        <Wrapper class="tool">
          <IconButton
            class="material-symbols-outlined"
            type="button"
            size="mini"
            on:click={() => activeEditor?.action(call(toggleStrongCommand.key))}
          >
            format_bold
          </IconButton>
          <Tooltip><b>太字</b></Tooltip>
        </Wrapper>
        <Wrapper class="tool">
          <IconButton
            class="material-symbols-outlined"
            type="button"
            size="mini"
            on:click={() => activeEditor?.action(call(toggleEmphasisCommand.key))}
          >
            format_italic
          </IconButton>
          <Tooltip><i>斜体</i></Tooltip>
        </Wrapper>
        <Wrapper class="tool">
          <IconButton
            class="material-symbols-outlined"
            type="button"
            size="mini"
            on:click={() => activeEditor?.action(call(toggleStrikethroughCommand.key))}
          >
            format_strikethrough
          </IconButton>
          <Tooltip><s>取り消し線</s></Tooltip>
        </Wrapper>
        <Wrapper class="tool">
          <IconButton
            class="material-symbols-outlined"
            type="button"
            size="mini"
            on:click={() => activeEditor?.action(call(createCodeBlockCommand.key))}
          >
            code
          </IconButton>
          <Tooltip>コードブロックを追加</Tooltip>
        </Wrapper>
        <Wrapper class="tool">
          <IconButton
            class="material-symbols-outlined"
            type="button"
            size="mini"
            on:click={() => activeEditor?.action(call(wrapInBlockquoteCommand.key))}
          >
            format_quote
          </IconButton>
          <Tooltip>引用</Tooltip>
        </Wrapper>
        <Wrapper class="tool">
          <IconButton
            class="material-symbols-outlined"
            type="button"
            size="mini"
            on:click={() => {
              imagePicker.pick().then((img) => {
                if (!img) return;
                activeEditor?.action(insert(`![](${img.previewUrl})\n\n![](a)`));
              });
            }}
          >
            add_photo_alternate
          </IconButton>
          <Tooltip>画像を挿入</Tooltip>
        </Wrapper>
        <Wrapper class="tool">
          <IconButton
            class="material-symbols-outlined"
            type="button"
            size="mini"
            on:click={() => {
              activeEditor?.action(call(wrapInHeadingCommand.key, 1));
            }}
          >
            format_h1
          </IconButton>
          <Tooltip>見出しを挿入</Tooltip>
        </Wrapper>
        <Wrapper class="tool">
          <IconButton
            class="material-symbols-outlined"
            type="button"
            size="mini"
            on:click={() => {
              activeEditor?.action(call(wrapInHeadingCommand.key, 2));
            }}
          >
            format_h2
          </IconButton>
          <Tooltip>副見出しを挿入</Tooltip>
        </Wrapper>
        <Wrapper class="tool">
          <IconButton
            class="material-symbols-outlined"
            type="button"
            size="mini"
            on:click={() => {
              activeEditor?.action(call(wrapInHeadingCommand.key, 3));
            }}
          >
            format_h3
          </IconButton>
          <Tooltip>セクション見出しを挿入</Tooltip>
        </Wrapper>
        <Wrapper class="tool">
          <IconButton
            class="material-symbols-outlined"
            type="button"
            size="mini"
            on:click={() => {
              activeEditor?.action(call(wrapInOrderedListCommand.key));
            }}
          >
            format_list_numbered
          </IconButton>
          <Tooltip>番号付きリスト</Tooltip>
        </Wrapper>
        <Wrapper class="tool">
          <IconButton
            class="material-symbols-outlined"
            type="button"
            size="mini"
            on:click={() => {
              activeEditor?.action(call(wrapInBulletListCommand.key));
            }}
          >
            format_list_bulleted
          </IconButton>
          <Tooltip>順序なしリスト</Tooltip>
        </Wrapper>
      </div>
    {/if}
  </div>
</div>

<style lang="scss">
  $sp-width: 456px;
  $tb-width: 880px;

  @media screen and (max-width: #{$tb-width}) {
    .toolbar.toolbar {
      top: 0;
    }
  }
  .toolbar {
    border-bottom: solid 1px lightgray;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    position: sticky;
    top: -2rem;
    background: white;
    z-index: 1000;
    margin-bottom: 1rem;
  }

  code.keystroke {
    word-break: keep-all;
    white-space: nowrap;
  }

  .editor-wrapper:not(.readonly) {
    .editor-root {
      border: 2px solid gray;
    }
  }

  .editor-root {
    border-radius: 0.5rem;
    padding: 0.5rem;
    min-height: max-content;
    background: white;

    &:focus-within {
      border-color: var(--primary-color);
    }

    :global(.editor) {
      min-height: 10rem;
      outline: none;
    }

    :global(h1),
    :global(h2),
    :global(h3),
    :global(h4),
    :global(h5),
    :global(h6) {
      border-bottom: solid 2px gray;

      &:before {
        content: '# ';
        color: var(--primary-color);
      }
    }

    :global(blockquote) {
      border-left: solid 2px gray;
      padding-left: 0.5rem;
      margin-left: 0.5rem;
    }

    :global(img) {
      max-width: 100%;
      max-height: 25rem;
    }

    :global(.read-more-preview) {
      border: solid 1px gray;
      padding: 0.5rem;
      border-radius: 4px;
      background-color: var(--primary-color);
      color: white;
      user-select: none;

      &:before {
        content: '続きを読む';
      }

      &:not(:first-of-type) {
        background-color: red;

        &:after {
          content: '(無効)';
        }
      }
    }
  }

  .editor-label {
    opacity: 0.5;
    font-size: 110%;
    margin: 0.5rem 0;
  }

  .editor-wrapper {
    cursor: text;
    margin-bottom: 2rem;
  }

  .editor-heading {
    display: grid;
    grid-template-columns: 1fr 1fr;

    .markdown-help {
      text-align: end;
      margin-top: auto;
      font-size: 90%;
    }
  }
</style>
